import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { subscribe } from 'lib/pusher';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import type {
  AriesMdbBundle,
  AriesMdbStepEvent,
  AriesRestoredRun,
  AriesRestoredStep,
} from 'components/aries-mdb-export/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Overall WorkflowProcess status event (shared across all CRM workflows).
const OVERALL_STATUS_EVENT = 'crm-workflow-process-status-changed';

// The final step's Pusher event. Its `processed` payload carries the finished
// bundle in `output` ({ filename, downloadUrl, expiresAt, fileSizeBytes }).
// This is the ONLY hardcoded event name — the checklist rows + labels remain
// fully server-driven; we only need to know which event yields the download.
const BUNDLE_STEP_EVENT = 'aries-package-bundle-status-changed';

// Same status → display-label map the DPS snapshot progress hook uses.
const STATUS_DISPLAY_MAP: Record<string, string> = {
  pending: 'Pending',
  processing: 'In Progress',
  processed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  blocked: 'Blocked',
  unchanged: 'Unchanged',
  unnecessary: 'Unnecessary',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AriesMdbProgressState {
  statusItems: StatusItem[];
  overallStatus: string;
  bundle: AriesMdbBundle | null;
}

type AriesMdbProgressAction =
  | { type: 'reset' }
  | { type: 'initialize'; steps: AriesMdbStepEvent[] }
  | {
      type: 'seed';
      steps: AriesRestoredStep[];
      overallStatus: string;
      bundle: AriesMdbBundle | null;
    }
  | {
      type: 'updateStep';
      name: string;
      status: string;
      details?: string | null;
    }
  | { type: 'setOverallStatus'; overallStatus: string }
  | { type: 'setBundle'; bundle: AriesMdbBundle };

// Per-step Pusher event payload (`notification_payload`). We only read the
// fields the checklist needs; `output` is present on the bundle step's
// `processed` event.
interface AriesMdbStepData {
  status?: string;
  details?: string | null;
  output?: AriesMdbBundle | null;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const ariesMdbProgressReducer = (
  state: AriesMdbProgressState,
  action: AriesMdbProgressAction
): AriesMdbProgressState => {
  switch (action.type) {
    case 'reset':
      return { statusItems: [], overallStatus: 'pending', bundle: null };
    case 'initialize':
      // Self-contained: a fresh run starts from a clean slate. Resetting
      // overallStatus + bundle here (not just statusItems) means a consumer
      // that sets new `events` WITHOUT first clearing them won't carry a stale
      // `processed` status or a stale download bundle into the new run —
      // hardening the INS-10553 hand-off that replaces the temporary trigger.
      return {
        overallStatus: 'pending',
        bundle: null,
        statusItems: action.steps.map((step) => ({
          label: step.label ?? '',
          status: 'pending',
          value: 'Pending',
          name: step.name,
        })),
      };
    case 'seed':
      // Rehydrate a restored run: seed each row to its persisted status (NOT
      // Pending), plus the run's overall status and any delivered download
      // bundle, in one shot. Mirrors the CRM tract wizard's
      // `initializeRestoredWorkflowAtom`. For an in-progress run the consumer
      // also keeps the channel live, so subsequent Pusher `updateStep` events
      // advance these already-seeded rows (never regressing a Completed one).
      return {
        overallStatus: action.overallStatus,
        bundle: action.bundle,
        statusItems: action.steps.map((step) => ({
          label: step.display_name ?? '',
          status: step.status,
          value: STATUS_DISPLAY_MAP[step.status] || step.status,
          name: step.event_name,
          details: step.failure_reason ?? undefined,
        })),
      };
    case 'updateStep':
      return {
        ...state,
        statusItems: state.statusItems.map((item) => {
          if (item.name !== action.name) return item;
          // Don't regress a processed step on out-of-order messages. Keyed on
          // the raw status (not the display label) so a relabel can't break it.
          if (item.status === 'processed' && action.status !== 'processed')
            return item;
          return {
            ...item,
            status: action.status,
            value: STATUS_DISPLAY_MAP[action.status] || action.status,
            // Only overwrite details when THIS event carries one; a later event
            // that omits details (e.g. a plain status tick after a failure) must
            // not wipe a previously-captured failure reason / context string.
            details: action.details ?? item.details,
          };
        }),
      };
    case 'setOverallStatus':
      return { ...state, overallStatus: action.overallStatus };
    case 'setBundle':
      return { ...state, bundle: action.bundle };
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseAriesMdbProgressParams {
  /**
   * Pusher channel to subscribe to for live updates, or null for no live
   * subscription. On a fresh kickoff this is the kickoff channel; on a restored
   * run the consumer passes the run's channel only when it is still in progress
   * (`should_subscribe`), and null for a terminal restored run.
   */
  channel: string | null;
  /** The kickoff response's notification events (1 overall + N steps). */
  events: AriesMdbStepEvent[] | null;
  /**
   * A restored run fetched from `latest_for_calculation`, used to seed the
   * checklist with each step's persisted status (plus overall status + any
   * delivered bundle) instead of resetting every row to Pending. Null for a
   * fresh kickoff or a calc with no run.
   */
  restore?: AriesRestoredRun | null;
}

interface UseAriesMdbProgressReturn {
  statusItems: StatusItem[];
  overallStatus: string;
  isBuilding: boolean;
  isComplete: boolean;
  isFailed: boolean;
  /** The finished download bundle, captured from the bundle step's `output`. */
  bundle: AriesMdbBundle | null;
}

/**
 * Subscribes to an AriesExportProcess Pusher channel and tracks per-step
 * progress for the AriesMdbExportModal's stages checklist. The checklist rows +
 * labels come from the server: either the kickoff `events` (fresh run — all rows
 * start Pending) or a restored run's `steps` (rehydrate — each row seeded to its
 * persisted status). On the bundle step's `processed` event — or, for a restored
 * completed run, from its re-minted `stepData` — the download is exposed via
 * `bundle`.
 *
 * Mount this in the persistent `useAriesMdbExportFlow` hook (which stays mounted
 * with the header) so progress survives the modal opening/closing (no lost
 * progress on reopen) and so the selected calc's run is re-derived from the
 * server on route change / reload.
 *
 * @param {UseAriesMdbProgressParams} params - Channel + kickoff events / restored run.
 * @return {UseAriesMdbProgressReturn} Progress state for the export modal checklist.
 */
export const useAriesMdbProgress = ({
  channel,
  events,
  restore = null,
}: UseAriesMdbProgressParams): UseAriesMdbProgressReturn => {
  const [state, dispatch] = useReducer(ariesMdbProgressReducer, {
    statusItems: [],
    overallStatus: 'pending',
    bundle: null,
  });

  // The checklist rows are exactly the events that carry a `step_type` (the
  // overall descriptor has none) — the same filter useTractWorkflow uses.
  // Labels stay server-driven; nothing about the step list is hardcoded here.
  const steps = useMemo<AriesMdbStepEvent[]>(
    () => (events ?? []).filter((event) => event.step_type),
    [events]
  );

  // Restored runs seed from their own `steps` (also filtered to real steps).
  const restoreSteps = useMemo<AriesRestoredStep[]>(
    () => (restore?.steps ?? []).filter((step) => step.type),
    [restore]
  );

  // The per-step Pusher event names to bind — from the restored run when
  // rehydrating, else from the kickoff events. Keeps live updates flowing after
  // an in-progress run is restored (whose kickoff `events` are long gone).
  const subscriptionStepNames = useMemo<string[]>(() => {
    if (restore) return restoreSteps.map((step) => step.event_name);
    return steps.map((step) => step.name);
  }, [restore, restoreSteps, steps]);

  const stepNamesRef = useRef(subscriptionStepNames);
  stepNamesRef.current = subscriptionStepNames;

  // Seed a restored run to its persisted per-step statuses; else initialize a
  // fresh run to all-Pending from the kickoff events; else reset when cleared.
  useEffect(() => {
    if (restore) {
      const bundleStep = restore.steps.find(
        (step) => step.event_name === BUNDLE_STEP_EVENT
      );
      dispatch({
        type: 'seed',
        steps: restoreSteps,
        overallStatus: restore.overall_status,
        bundle: bundleStep?.stepData ?? null,
      });
    } else if (steps.length > 0) {
      dispatch({ type: 'initialize', steps });
    } else {
      dispatch({ type: 'reset' });
    }
  }, [restore, restoreSteps, steps]);

  // Per-step event handler: updates the row and, on the bundle step's
  // `processed` event, captures the download `output`.
  const createStepHandler = useCallback(
    (eventName: string) => (data: AriesMdbStepData) => {
      if (!data?.status) return;
      dispatch({
        type: 'updateStep',
        name: eventName,
        status: data.status,
        details: data.details,
      });
      if (
        eventName === BUNDLE_STEP_EVENT &&
        data.status === 'processed' &&
        data.output
      ) {
        dispatch({ type: 'setBundle', bundle: data.output });
      }
    },
    []
  );

  // Single subscription per channel: bind the overall event + all per-step
  // handlers once, and on cleanup (channel-change / unmount) unbind them and
  // unsubscribe. Mirrors useTractWorkflow's single subscribe/unsubscribe
  // lifecycle.
  //
  // Keyed ONLY on `channel` (plus the stable `createStepHandler`, which just
  // dispatches) — deliberately NOT on `overallStatus`. This is load-bearing:
  // if the effect re-ran on the pending→processing→processed transitions and
  // unbound handlers once overall reached `processed`, a bundle-step
  // `processed` event that Pusher delivered AFTER the overall terminal event
  // would be torn down and its `output.downloadUrl` lost forever (Pusher does
  // not guarantee cross-event ordering). Keeping the handlers bound for the
  // channel's whole life means bundle capture survives any terminal-event order.
  //
  // The subscribe-after-fire race is now closed by server-sourced restore: the
  // consumer re-derives the selected calc's run from `latest_for_calculation`
  // (which seeds terminal state directly), and only passes a live `channel` for
  // a run that is still in progress — so a completed/failed run never depends on
  // catching a Pusher event, and an in-progress run keeps advancing live.
  useEffect(() => {
    if (!channel) return;

    const subscription = subscribe(channel);

    subscription.bind(
      OVERALL_STATUS_EVENT,
      (data: { overall_status?: string }) => {
        if (data?.overall_status) {
          dispatch({
            type: 'setOverallStatus',
            overallStatus: data.overall_status,
          });
        }
      }
    );

    const stepNames = stepNamesRef.current;
    stepNames.forEach((name) => {
      subscription.bind(name, createStepHandler(name));
    });

    return () => {
      subscription.unbind(OVERALL_STATUS_EVENT);
      stepNames.forEach((name) => {
        subscription.unbind(name);
      });
      subscription.unsubscribe();
    };
  }, [channel, createStepHandler]);

  return useMemo(
    () => ({
      statusItems: state.statusItems,
      overallStatus: state.overallStatus,
      isBuilding:
        state.overallStatus === 'processing' ||
        state.overallStatus === 'pending',
      isComplete: state.overallStatus === 'processed',
      isFailed: state.overallStatus === 'failed',
      bundle: state.bundle,
    }),
    [state]
  );
};
